import {
  ProductPurchaseType,
  productShopifyIdMap,
} from 'content/products';
import { ONE_TIME_M1_OFFER } from 'lib/commerce/cart/campaignLinePresence';
import { CartLineAttribute, CartLineInput } from 'lib/commerce/cart/types';
import { CartDiscountOptions } from 'lib/commerce/cart/policies';
import { upsertMaxQuantityAttribute } from 'lib/commerce/cart/policies/cartLineHelpers';
import { addToCartArgsType, ProductType } from '../types';

export interface NormalizedAdd {
  lineItemsToAdd: CartLineInput[];
  discountCodes: string[];
}

/** Pre-pipeline transform of raw args into initial PolicyCartInput line items + seeded discount codes. */
export function normalizeAddToCartArgs(
  argsArray: ReadonlyArray<addToCartArgsType>,
  currentDiscountCodes: ReadonlyArray<string>,
): NormalizedAdd {
  const lineItemsToAdd: CartLineInput[] = [];
  const discountCodes: string[] = [...currentDiscountCodes];

  for (const args of argsArray) {
    if (args.discountCode) discountCodes.push(args.discountCode);

    const noteAttributes = extractNoteAttributes(args);
    const requestedMax = normalizeMaxQuantityInput(args.params?.maxQuantity);

    if (args.pid !== undefined) {
      const pidConfig = productShopifyIdMap[args.pid as ProductPurchaseType];
      if (!pidConfig) continue;
      lineItemsToAdd.push({
        merchandiseId: pidConfig.shopifyVariantId,
        quantity: 1,
        attributes: upsertMaxQuantityAttribute(
          [{ key: '_product_type', value: pidConfig.productType }, ...noteAttributes],
          requestedMax,
        ),
      });
    } else if (args.shopifyVariantId !== undefined) {
      const productTypeAttr: CartLineAttribute[] = args.params?.productType
        ? [{ key: '_product_type', value: args.params.productType }]
        : args.productType
        ? [{ key: '_product_type', value: args.productType }]
        : [];
      lineItemsToAdd.push({
        merchandiseId: args.shopifyVariantId,
        quantity: 1,
        attributes: upsertMaxQuantityAttribute(
          [...productTypeAttr, ...noteAttributes],
          requestedMax,
        ),
        ...(args.sellingPlanId ? { sellingPlanId: args.sellingPlanId } : {}),
      });
    }
  }

  return { lineItemsToAdd, discountCodes };
}

/** Product-type flags for the discount engine. */
export function computeProductTypeFlags(
  argsArray: ReadonlyArray<addToCartArgsType>,
): CartDiscountOptions {
  const flags: CartDiscountOptions = {};
  for (const args of argsArray) {
    if (args.productType === ProductType.M1) flags.isM1ProductTypePresent = true;
    else if (args.productType === ProductType.RING) flags.isRingProductTypePresent = true;
    else if (args.productType === ProductType.RING_PRO) flags.isRingProProductTypePresent = true;
    else if (args.productType === ProductType.UHX) flags.isUhxProductTypePresent = true;
    if (args.params?.productType === 'HOME') flags.isHomeProductTypePresent = true;
    if (
      args.params?.noteAttributes?.some(
        (attr) => attr.key === '_m1_offer' && attr.value === ONE_TIME_M1_OFFER,
      )
    ) {
      flags.isOneTimeM1Present = true;
    }
  }
  return flags;
}

/** Trade-in is detected only when both device and serial note attributes are present. */
export function computeHasTradeIn(
  argsArray: ReadonlyArray<addToCartArgsType>,
): boolean {
  return argsArray.some((args) => {
    const attrs = args.params?.noteAttributes;
    if (!attrs) return false;
    const hasDevice = attrs.some((a) => a.key === 'trade_in_device' && a.value?.trim());
    const hasSerial = attrs.some((a) => a.key === 'trade_in_serial_number' && a.value?.trim());
    return Boolean(hasDevice && hasSerial);
  });
}

function extractNoteAttributes(args: addToCartArgsType): CartLineAttribute[] {
  if (!Array.isArray(args.params?.noteAttributes)) return [];
  return (args.params?.noteAttributes as Array<Record<string, string>>)
    .map((noteAttr) => ({
      key: noteAttr.key ?? noteAttr['key'],
      value: noteAttr.value ?? noteAttr['value'],
    }))
    .filter(
      (noteAttr): noteAttr is CartLineAttribute =>
        typeof noteAttr.key === 'string' &&
        noteAttr.key.trim().length > 0 &&
        typeof noteAttr.value === 'string' &&
        noteAttr.value.trim().length > 0,
    );
}

function normalizeMaxQuantityInput(value?: number): number | null {
  if (!Number.isFinite(value)) return null;
  const normalized = Math.floor(Number(value));
  return normalized > 0 ? normalized : null;
}
